import {
  PersonRecordCreateFetch,
  PersonRecordFetch,
  PersonRecordUpdateFetch,
} from './Record.type';
import { createAsyncThunk, ThunkDispatch } from '@reduxjs/toolkit';
import recordService from './Record.service';
import { destroyPersonThunk, getCandidateInfoThunk } from '../Candidate.thunk';
import { syncAttachmentsThunk } from '../../Attachment/Attachment.thunk';
import {
  AttachEditState,
  AttachFetchType,
} from '../../Attachment/Attachment.type';
import { PERSON_RECORD_ATTACH_OBJECT } from '../../../constants/attachment.constant';
import { errorHandlerThunk } from '../../App/App.thunk';
import { TagSyncPayload } from '../../Tag/Tag.type';
import { syncTagsThunk } from '../../Tag/Tag.thunk';
import { ERROR_CODE } from '../../../constants/error.constant';
import { RECORDS_PER_PAGE } from '../../../constants/candidate.constant';

export const getCandidateRecordListThunk = createAsyncThunk(
  'records/getCandidateRecordListThunk',
  async (params: PersonRecordFetch, thunkApi) => {
    const response = await recordService.getList(params);
    const { data } = response;
    return data;
  },
);

export const getCandidateRecordInfoThunk = createAsyncThunk(
  'records/getCandidateRecordInfoThunk',
  async (params: PersonRecordUpdateFetch, thunkApi) => {
    const response = await recordService.getInfo(params);
    return response.data.data;
  },
);

export const deleteRecordInfoThunk =
  (params: PersonRecordUpdateFetch) =>
  (dispatch: ThunkDispatch<any, any, any>) => {
    return recordService
      .delete(params)
      .then(() => {
        dispatch(getCandidateInfoThunk(params.personId));
        dispatch(getCandidateRecordListThunk({
          personId: params.personId,
          perPage: RECORDS_PER_PAGE,
          page: 1
        }));

        return true;
      })
      .catch(err => {
        if (err.response.status === ERROR_CODE.CONFLICT) {
          dispatch(destroyPersonThunk(params.personId));
          return true;
        }
        dispatch(errorHandlerThunk(err));

        return false;
      });
  };

export const createPersonRecordThunk =
  (params: PersonRecordCreateFetch) =>
  (dispatch: ThunkDispatch<any, any, any>) => {
    return new Promise(resolve =>
      recordService
        .create(params)
        .then(async ({ data: info }: any) => {
          const { data } = info;

          if (params.attachments && params.attachments.length > 0) {
            const attachments: AttachFetchType[] = params.attachments.map(
              (attach: AttachEditState) =>
                ({
                  uuid: attach.uuid,
                  tmpUUID: attach.uuid ? undefined : attach.tmpUUID,
                  author_id: attach.author_id,
                  type: attach.type,
                  url: attach.url,
                  file: attach.file,
                  is_private: attach.isPrivate ? 1 : 0,
                } as AttachFetchType),
            );

            await dispatch(
              syncAttachmentsThunk(
                {
                  type: PERSON_RECORD_ATTACH_OBJECT,
                  id: data.id,
                },
                attachments,
              ),
            );
          }

          if (params.tags && params.tags.length > 0) {
            const payload: TagSyncPayload = {
              objectType: PERSON_RECORD_ATTACH_OBJECT,
              objectId: data.id,
              tags: params.tags.map(tag => tag.id),
            };
            await dispatch(syncTagsThunk(payload));
          }

          resolve(data || true);
          dispatch(getCandidateInfoThunk(params.personId));
          dispatch(getCandidateRecordListThunk({
            personId: params.personId,
            perPage: RECORDS_PER_PAGE,
            page: 1
          }));
        })
        .catch(err => {
          dispatch(errorHandlerThunk(err));
          resolve(false);
        }),
    );
  };

export const updatePersonRecordThunk =
  (params: PersonRecordUpdateFetch) =>
  (dispatch: ThunkDispatch<any, any, any>) => {
    return new Promise(resolve =>
      recordService
        .update(params)
        .then(async () => {
          const hasNewAttach = params.attachments?.some(({ uuid }) => !uuid);
          const hasAttachToDelete = params.attachments?.some(({ isDeleted }) => isDeleted);

          if (params.attachments && params.attachments.length && (hasNewAttach || hasAttachToDelete)) {
            const attachments: AttachFetchType[] = params.attachments.map(
              (attach: AttachEditState) =>
                ({
                  uuid: attach.uuid,
                  tmpUUID: attach.uuid ? undefined : attach.tmpUUID,
                  author_id: attach.author_id,
                  type: attach.type,
                  url: attach.url,
                  file: attach.file,
                  is_private: attach.isPrivate ? 1 : 0,
                  is_deleted: attach.isDeleted ? 1 : 0,
                } as AttachFetchType),
            );
            await dispatch(
              syncAttachmentsThunk(
                {
                  type: PERSON_RECORD_ATTACH_OBJECT,
                  id: params.recordId,
                },
                attachments,
              ),
            );
          }

          const payload: TagSyncPayload = {
            objectType: PERSON_RECORD_ATTACH_OBJECT,
            objectId: params.recordId,
            tags: (params.tags || []).map(tag => tag.id),
          };

          await dispatch(syncTagsThunk(payload));
          resolve(true);
          dispatch(getCandidateInfoThunk(params.personId));
          dispatch(getCandidateRecordListThunk({
            personId: params.personId,
            perPage: RECORDS_PER_PAGE,
            page: 1
          }));
        })
        .catch(err => {
          dispatch(errorHandlerThunk(err));
          resolve(false);
        }),
    );
  };
